import React, { useState, useEffect } from "react";
import "./RelatorioMeta.css";
import {ComposedChart,
  LabelList, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from "recharts";
import { ArrowUpFromLine, ArrowDownFromLine } from "lucide-react";
import { formatarDataBrasil } from "../../utils/utils";
import DateRangePresets from "../../components/DateRangePresets";
import RelatorioSkeleton from "../../components/RelatorioSkeleton";

function RelatorioMetaCompleto() {
  const [dados, setDados] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState("");
  const [anuncios, setAnuncios] = useState([]);
  const hoje = new Date().toISOString().slice(0, 10);
  const primeiroDiaDoMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const [dataInicio, setDataInicio] = useState(primeiroDiaDoMes);
  const [dataFim, setDataFim] = useState(hoje);
  const [carregando, setCarregando] = useState(false);
  const [totais, setTotais] = useState({
    spend: 0, impressions: 0, reach: 0, clicks: 0,
    engajamento: 0, video_views: 0, conversas: 0
  });
  const [campanhaSelecionada, setCampanhaSelecionada] = useState("todas");
  const [iaOpen, setIaOpen] = useState(true);
const [iaLoading, setIaLoading] = useState(false);
const [iaError, setIaError] = useState("");
const [iaResult, setIaResult] = useState(null);
const [iaMeta, setIaMeta] = useState({ cached: false, generated_at: "", scope: null, warning: "" });
const [followersDiario, setFollowersDiario] = useState([]);


const CARD_COLOR = {
  "Resumo (linguagem simples)": "blue",
  "Resumo (para gestor de tráfego)": "purple",
  "Tradução para o negócio": "green",
  "Checagem de desperdício": "orange",
  "Geografia": "teal",
  "O que funcionou": "green",
  "O que prejudicou": "red",
  "Orçamento: como alocar": "blue",
  "Criativo e copy": "purple",
  "Funil e rastreamento": "teal",
  "Hoje (30 min)": "green",
  "Próximas 48 horas": "blue",
  "Próximas 2 semanas": "purple",
  "Regras de escala": "green",
  "Regras de corte": "red",
  "Riscos e atenção": "orange",
  "Limitações do relatório": "gray",
};



  // aceita item com campaign_id (número) ou sem id
const pertenceACampanhaSelecionada = item =>
  !item.campaign_id ||
  campanhaSelecionada === "todas" ||
  String(item.campaign_id) === campanhaSelecionada ||
  item.campaign_id === Number(campanhaSelecionada);

const isCampanhaSelecionada = campanha_id =>
  campanhaSelecionada === "todas" ||
  String(campanha_id) === campanhaSelecionada ||
  campanha_id === Number(campanhaSelecionada);


  useEffect(() => {
  async function carregarThumbnails() {
    if (!dados?.meta_ads_ads?.data) return;

    const ads = dados.meta_ads_ads.data
      .filter((ad) => isCampanhaSelecionada(ad.campaign_id))
      .map((anuncio) => {
        const adItem = {
          id: anuncio.ad_id,
          nome: anuncio.ad_name || "Sem Nome",
          alcance: parseInt(anuncio.reach || 0),
          impressoes: parseInt(anuncio.impressions || 0),
          cliques: parseInt(anuncio.clicks || 0),
          valorGasto: parseFloat(anuncio.spend || 0),
          campaign_id: anuncio.campaign_id,
          campaign_name: anuncio.campaign_name,
          adset_id: anuncio.adset_id,
          adset_name: anuncio.adset_name,
          thumbnail: null,
          conversas: 0,
        };

        (anuncio.actions || []).forEach((a) => {
          if (a.action_type === "onsite_conversion.messaging_conversation_started_7d") {
            adItem.conversas = parseInt(a.value || 0);
          }
        });

        return adItem;
      });

    const adsComThumbs = await Promise.all(
      ads.map(async (ad) => {
        try {
          const resp = await fetch(
            `https://welaunchyou.com.br/crm-wly/api/integracoes/meta_thumbnail_ad.php?ad_id=${ad.id}`
          );
          const thumbData = await resp.json();
          if (thumbData.success && thumbData.thumbnail_url) {
            ad.thumbnail = thumbData.thumbnail_url;
          }
        } catch (e) {
          console.error(`Erro ao carregar thumbnail do anúncio ${ad.id}`, e);
        }
        return ad;
      })
    );

    setAnuncios(adsComThumbs.sort((a, b) => b.conversas - a.conversas));
  }

  carregarThumbnails();
}, [dados, campanhaSelecionada]);


useEffect(() => {
  async function carregarUsuarios() {
    try {
      const response = await fetch(
        "https://welaunchyou.com.br/crm-wly/api/integracoes/meta_listar_vinculo.php",
        {
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success) {
        const usuariosOrdenados = [...(data.data || [])].sort((a, b) => {
          const nomeA = (a.facebook_page_name || a.nome_usuario || "").trim();
          const nomeB = (b.facebook_page_name || b.nome_usuario || "").trim();

          return nomeA.localeCompare(nomeB, "pt-BR", {
            sensitivity: "base",
            ignorePunctuation: true,
          });
        });

        setUsuarios(usuariosOrdenados);
      }
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  }

  carregarUsuarios();
}, []);

async function gerarAnaliseIA() {
  try {
    setIaError("");
    setIaLoading(true);

    const selecionado = usuarios.find(u => u.id === Number(usuarioSelecionado));
    if (!selecionado) throw new Error("Nenhum usuário selecionado encontrado!");

    const params = new URLSearchParams({
      data_inicio: dataInicio,
      data_fim: dataFim,
      ad_account_id: selecionado.ad_account_id,
      page_id: selecionado.facebook_page_id,
      ig_user_id: selecionado.instagram_id,
    });

    if (campanhaSelecionada && campanhaSelecionada !== "todas") {
      params.set("campaign_id", campanhaSelecionada);
    }

    const r = await fetch(`/crm-wly/api/integracoes/analisar_meta.php?${params.toString()}`, {
      credentials: "include",
    });

    const j = await r.json().catch(() => null);

    if (r.status === 429) {
      throw new Error("No momento não foi possível gerar uma nova análise. Tente novamente amanhã.");
    }

    if (!r.ok || !j?.success) {
      throw new Error(j?.error || `Erro HTTP ${r.status}`);
    }

    // ✅ Agora guardamos envelope + miolo
    setIaResult(j.analysis || null);
    setIaMeta({
      cached: !!j.cached,
      generated_at: j.generated_at || "",
      scope: j.scope || null,
      warning: j.warning || "",
    });

    setIaOpen(true);
  } catch (e) {
    setIaError(e?.message || "Erro ao gerar análise");
  } finally {
    setIaLoading(false);
  }
}

async function carregarRelatorio(override = {}) {
  if (!usuarioSelecionado) return;

const vinculoId = Number(usuarioSelecionado);
const selecionado = usuarios.find((u) => Number(u.id) === vinculoId);

  if (!selecionado) {
    console.error("Nenhum usuário selecionado encontrado!");
    return;
  }

  // ✅ usa override se vier, senão usa o state atual
  const di = override.data_inicio ?? dataInicio;
  const df = override.data_fim ?? dataFim;

  setCarregando(true);

  try {
    const params = new URLSearchParams({
      data_inicio: di,
      data_fim: df,
      ad_account_id: String(selecionado.ad_account_id || ""),
      page_id: String(selecionado.facebook_page_id || ""),
      ig_user_id: String(selecionado.instagram_id || ""),
    });

    const urlRelatorio = `https://welaunchyou.com.br/crm-wly/api/integracoes/meta_relatorio.php?${params.toString()}`;

const paramsFollowers = new URLSearchParams({
  data_inicio: di,
  data_fim: df,
  ig_user_id: String(selecionado.instagram_id || ""),
  usuario_id: String(selecionado.usuario_id || selecionado.id || ""),
});

const urlFollowers = `https://welaunchyou.com.br/crm-wly/api/integracoes/meta_followers_diario.php?${paramsFollowers.toString()}`;

const [resRelatorio, resFollowers] = await Promise.all([
  fetch(urlRelatorio, { credentials: "include" }),
  fetch(urlFollowers, { credentials: "include" }),
]);

const jsonRelatorio = await resRelatorio.json().catch(() => null);
const jsonFollowers = await resFollowers.json().catch(() => null);

if (!resRelatorio.ok) {
  console.error("Erro HTTP relatório:", resRelatorio.status, jsonRelatorio);
  return;
}

if (!jsonRelatorio?.success) {
  console.error("Erro na resposta do relatório:", jsonRelatorio?.error || "Resposta inválida");
  return;
}

if (!resFollowers.ok) {
  console.error("Erro HTTP followers:", resFollowers.status, jsonFollowers);
  setFollowersDiario([]);
} else if (!jsonFollowers?.success) {
  console.error("Erro na resposta de followers:", jsonFollowers?.error || "Resposta inválida");
  setFollowersDiario([]);
} else {
  setFollowersDiario(jsonFollowers?.data?.series || []);
}

setDados(jsonRelatorio.data);
setCampanhaSelecionada("todas");
  } catch (err) {
    console.error("Erro ao carregar relatório:", err);
  } finally {
    setCarregando(false);
  }
}


  const formatarMoeda = (valor) => Number(valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const formatarNumero = (valor) => Number(valor || 0).toLocaleString("pt-BR");
  const formatarPorcentagem = (valor) => (Number(valor || 0).toFixed(2) + "%").replace(".", ",");

  const calcularTotais = () => {
    if (!dados?.meta_ads?.data) return {
      spend:0, impressions:0, reach:0, clicks:0,
      engajamento:0, video_views:0, conversas:0
    };

    return dados.meta_ads.data
      .filter((camp) => isCampanhaSelecionada(camp.campaign_id))
      .reduce(
        (tot, camp) => {
          tot.spend += parseFloat(camp.spend || 0);
          tot.impressions += parseInt(camp.impressions || 0);
          tot.reach += parseInt(camp.reach || 0);
          tot.clicks += parseInt(camp.clicks || 0);
          (camp.actions || []).forEach((a) => {
            if (a.action_type === "post_engagement") tot.engajamento += parseInt(a.value || 0);
            if (a.action_type === "video_view") tot.video_views += parseInt(a.value || 0);
            if (a.action_type === "onsite_conversion.messaging_conversation_started_7d") tot.conversas += parseInt(a.value || 0);
          });
          return tot;
        },
        {spend:0, impressions:0, reach:0, clicks:0, engajamento:0, video_views:0, conversas:0}
      );
  };

  useEffect(() => {
    if (!dados) return;
    setTotais(calcularTotais());
  }, [dados, campanhaSelecionada]);

  useEffect(() => {
  setIaResult(null);
  setIaMeta({ cached: false, generated_at: "", scope: null, warning: "" });
  setIaError("");
}, [usuarioSelecionado, dataInicio, dataFim, campanhaSelecionada]);


  const ctrMedia = (totais.clicks / totais.impressions) * 100 || 0;
  const cplMedio = totais.spend / (totais.conversas || 1);
  const frequenciaMedia = totais.reach ? totais.impressions / totais.reach : 0;

  const conversasPorPlataforma = (() => {
  const base = dados?.meta_ads_conversas_plataforma;

  if (!base) {
    return {
      facebook: 0,
      instagram: 0,
      messenger: 0,
      audience_network: 0,
      outros: 0,
      total: 0,
    };
  }

  if (campanhaSelecionada !== "todas") {
    const campanha = (base.campanhas || []).find(
      (c) => String(c.campaign_id) === String(campanhaSelecionada)
    );

    return {
      facebook: Number(campanha?.facebook || 0),
      instagram: Number(campanha?.instagram || 0),
      messenger: Number(campanha?.messenger || 0),
      audience_network: Number(campanha?.audience_network || 0),
      outros: Number(campanha?.outros || 0),
      total: Number(campanha?.total || 0),
    };
  }

  const facebook = Number(base.facebook || 0);
  const instagram = Number(base.instagram || 0);
  const messenger = Number(base.messenger || 0);
  const audience_network = Number(base.audience_network || 0);
  const outros = Number(base.outros || 0);

  return {
    facebook,
    instagram,
    messenger,
    audience_network,
    outros,
    total: facebook + instagram + messenger + audience_network + outros,
  };
})();

const dadosGraficoConversasPlataforma = [
  { nome: "Facebook", conversas: conversasPorPlataforma.facebook },
  { nome: "Instagram", conversas: conversasPorPlataforma.instagram },
  { nome: "Messenger", conversas: conversasPorPlataforma.messenger },
  { nome: "Audience Network", conversas: conversasPorPlataforma.audience_network },
  { nome: "Outros", conversas: conversasPorPlataforma.outros },
].filter((item) => item.conversas > 0);

const PASTEL_BY_TONE = {
  blue:   "#EAF2FF",
  green:  "#EFFFF4",
  orange: "#FFF4EA",
  purple: "#F6EEFF",
  teal:   "#EAFBF7",
  red:    "#FFEFF3",
  gray:   "#F3F5F7",
};

function resetarRelatorioVisual() {
  setFollowersDiario([]);
  setDados(null);
  setAnuncios([]);
  setCampanhaSelecionada("todas");
  setTotais({
    spend: 0, impressions: 0, reach: 0, clicks: 0,
    engajamento: 0, video_views: 0, conversas: 0
  });
}

function ymdLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function exportarPDF() {
  const tituloOriginal = document.title;

  document.title = `Relatorio Meta - ${dataInicio} a ${dataFim}`;

  // Garante que a análise da IA apareça no PDF caso exista
  setIaOpen(true);

  const restaurarTitulo = () => {
    document.title = tituloOriginal;
    window.removeEventListener("afterprint", restaurarTitulo);
  };

  window.addEventListener("afterprint", restaurarTitulo);

  setTimeout(() => {
    window.print();
  }, 300);
}


function renderBullets(title, arr, tone = "gray") {
  if (!Array.isArray(arr) || arr.length === 0) return null;

  const bg = PASTEL_BY_TONE[tone] || PASTEL_BY_TONE.gray;

  return (
    <div className="gads-ai-card" style={{ background: bg }}>
      <div className="gads-ai-card-title">{title}</div>
      <ul className="gads-ai-ul">
        {arr.map((item, idx) => {
          const texto =
            typeof item === "string"
              ? item
              : item?.text || item?.description || item?.title || JSON.stringify(item);

          return <li key={idx}>{texto}</li>;
        })}
      </ul>
    </div>
  );
}
  return (
  <>
  <div className="relatorio-meta-page">
    <h1 className="relatorio-meta-titulo">Relatório de Anúncios Meta</h1>

    <div className="relatorio-meta-scroll">
      <div id="relatorio-meta-pdf" className="relatorio-meta-wrapper">

            <div className="gads-logo-wrap">
            <img
              className="gads-logo"
              src={`https://welaunchyou.com.br/crm-wly/uploads/logos/meta-logo.png`}
              alt="Google Ads"
            />
          </div>

      {/* 🔵 Filtros */}
      <div className="relatorio-meta-filtro">
        <div className="relatorio-meta-item">
          <label>Selecione o Usuário:</label>
          <select
            value={usuarioSelecionado}
            onChange={(e) => {
              setUsuarioSelecionado(e.target.value);
              setCampanhaSelecionada("todas");
              resetarRelatorioVisual();
            }}
          >
            <option value="">-- Escolha um usuário --</option>
            {usuarios.map((user) => (
              <option key={user.id} value={user.id}>
                {user.facebook_page_name || "Página não informada"}
              </option>
            ))}
          </select>
        </div>

<div className="relatorio-meta-item">
  <DateRangePresets
    label="Período"
    startDate={new Date(`${dataInicio}T12:00:00`)}
    endDate={new Date(`${dataFim}T12:00:00`)}
    onChange={({ start, end }) => {
      setDataInicio(ymdLocal(start));
      setDataFim(ymdLocal(end));
      resetarRelatorioVisual();
    }}
    onApply={({ start, end }) => {
      carregarRelatorio({
        data_inicio: ymdLocal(start),
        data_fim: ymdLocal(end),
      });
    }}
  />
</div>



        <div className="relatorio-meta-item">
          <button onClick={carregarRelatorio}>Gerar Relatório</button>
        </div>
        
          {dados && (
  <div className="relatorio-meta-item">
    <button
      type="button"
      className="btn-exportar-pdf"
      onClick={exportarPDF}
    >
      Exportar PDF
    </button>
  </div>
)}


      </div>

      {carregando && (
        <RelatorioSkeleton />
      )}

      {/* Filtro de Campanha */}
        {dados?.meta_ads?.data?.length > 0 && (
          <div className="relatorio-meta-item">
            <label>Campanha:</label>
            <select
              value={campanhaSelecionada}
              onChange={(e) => setCampanhaSelecionada(e.target.value)}
            >
              <option value="todas">Todas as Campanhas</option>
              {dados.meta_ads.data.map((c, i) => (
                <option key={i} value={c.campaign_id}>
                  {c.campaign_name}
                </option>
              ))}
            </select>
          </div>
        )}

      {/* 🔵 Visão Geral */}
{dados && (
  <>
    <h2 className="relatorio-meta-subtitulo" style={{ borderBottom: "none" }}>
      Visão Geral
    </h2>
    <p className="relatorio-meta-explicativo">
      Este painel apresenta as métricas de desempenho de{" "}
      <b>{formatarDataBrasil(dataInicio)}</b> até{" "}
      <b>{formatarDataBrasil(dataFim)}</b>.
    </p>

    <div className="relatorio-meta-cards">
      <div className="relatorio-meta-card">
        <span>Cliques</span>
        <strong>{formatarNumero(totais.clicks)}</strong>
        <p className="relatorio-meta-texto-cards">
          Todos os cliques em seus anúncios
        </p>
      </div>

    <div className="relatorio-meta-card">
      <span>CPL Médio</span>
      <strong>{formatarMoeda(cplMedio)}</strong>
      <p className="relatorio-meta-texto-cards">
        Custo por cada lead/conversa gerada
      </p>
    </div>


      <div className="relatorio-meta-card">
        <span>Alcance</span>
        <strong>{formatarNumero(totais.reach)}</strong>
        <p className="relatorio-meta-texto-cards">
          Pessoas alcançadas no período
        </p>
      </div>

      <div className="relatorio-meta-card">
        <span>Impressões</span>
        <strong>{formatarNumero(totais.impressions)}</strong>
        <p className="relatorio-meta-texto-cards">
          Quantidade de vezes que seus anúncios apareceram
        </p>
      </div>

      <div className="relatorio-meta-card">
        <span>Frequência Média</span>
        <strong>{frequenciaMedia.toFixed(2)}</strong>
        <p className="relatorio-meta-texto-cards">
          Média de vezes que cada pessoa viu seus anúncios
        </p>
      </div>

      <div className="relatorio-meta-card">
        <span>Valor Investido</span>
        <strong>{formatarMoeda(totais.spend)}</strong>
        <p className="relatorio-meta-texto-cards">
          Investimento em tráfego pago total do período
        </p>
      </div>

      <div className="relatorio-meta-card">
        <span>CPM</span>
        <strong>
          {totais.reach > 0
            ? formatarMoeda(totais.spend / (totais.reach / 1000))
            : "-"}
        </strong>
        <p className="relatorio-meta-texto-cards">
          Custo por 1.000 pessoas alcançadas
        </p>
      </div>

      <div className="relatorio-meta-card-conversas">
        <span>Conversas Iniciadas</span>
        <strong>{formatarNumero(totais.conversas)}</strong>
        <p className="relatorio-meta-texto-cards">
          Conversas iniciadas a partir dos seus anúncios
        </p>
      </div>

      <div className="relatorio-meta-card">
        <span>Visualizações de Vídeo</span>
        <strong>{formatarNumero(totais.video_views)}</strong>
        <p className="relatorio-meta-texto-cards">
          Pessoas que assistiram algum dos seus vídeos
        </p>
      </div>
    </div>

    <hr className="linha-pontilhada" />


{/* 🔵 Gráfico 1: Evolução de Alcance, CPL e Investimento por Dia */}
<h2 style={{ borderBottom: "none" }} className="relatorio-meta-subtitulo">
  Evolução de Alcance, CPL e Investimento por Dia
</h2>

<p className="relatorio-meta-explicativo">
  Passe o mouse sobre as linhas para visualizar os números da data.
</p>

<div className="relatorio-meta-grafico">
  <ResponsiveContainer width="100%" height={320}>
 <ComposedChart
  margin={{ top: 10, right: 55, left: 0, bottom: 5 }}
  data={(dados.meta_ads_daily?.data || [])
    .filter(pertenceACampanhaSelecionada)
    .map((item) => ({
      date: item.date_start,
      reach: parseInt(item.reach || 0, 10),
      cpl: parseFloat(item.cpl || 0),
      spend: parseFloat(item.spend || 0),
    }))}
>
  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />

  <XAxis
    dataKey="date"
    tick={{ fontSize: 11, fill: "#666" }}
    tickMargin={8}
    tickFormatter={(tick) => {
      const [ano, mes, dia] = String(tick).split("-");
      return `${dia}/${mes}`;
    }}
  />

  <YAxis
    yAxisId="left"
    orientation="left"
    tick={{ fontSize: 11, fill: "#666" }}
    tickMargin={8}
  />

  <YAxis
    yAxisId="cpl"
    orientation="right"
    tick={{ fontSize: 10, fill: "#dc2626" }}
    tickMargin={8}
    tickFormatter={(v) => Math.round(v)}
    domain={[0, "dataMax + 5"]}
    axisLine={false}
    tickLine={false}
    label={{
      value: "CPL (R$)",
      angle: 90,
      position: "insideRight",
      offset: 10,
      style: { fontSize: 11, fill: "#dc2626" },
    }}
  />

  <YAxis
    yAxisId="spend"
    hide={true}
    domain={[0, "auto"]}
  />

  <Tooltip
    contentStyle={{
      borderRadius: "10px",
      border: "1px solid #e5e7eb",
      fontSize: "12px",
    }}
    formatter={(value, name) => {
      if (name === "CPL") return [`R$ ${Number(value).toFixed(2)}`, "CPL"];
      if (name === "Investimento") return [`R$ ${Number(value).toFixed(2)}`, "Investimento"];
      return [Number(value).toLocaleString("pt-BR"), "Alcance"];
    }}
    labelFormatter={(label) => {
      const [ano, mes, dia] = String(label).split("-");
      return `${dia}/${mes}/${ano}`;
    }}
  />

  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />

  <Bar
    yAxisId="spend"
    dataKey="spend"
    name="Investimento"
    fill="#10b981"
    fillOpacity={0.25}
    radius={[4, 4, 0, 0]}
  />

  <Line
    yAxisId="left"
    type="monotone"
    dataKey="reach"
    stroke="#007bff"
    name="Alcance"
    strokeWidth={2}
    dot={false}
    activeDot={{ r: 5 }}
  />

  <Line
    yAxisId="cpl"
    type="monotone"
    dataKey="cpl"
    stroke="#e63946"
    name="CPL"
    strokeWidth={2}
    dot={false}
    activeDot={{ r: 5 }}
  />
</ComposedChart>
  </ResponsiveContainer>
</div>

<hr className="linha-pontilhada" />

{followersDiario.length > 0 && (
  <>
    <h2 style={{ borderBottom: "none" }} className="relatorio-meta-subtitulo">
      Seguidores ganhos por dia no Instagram
    </h2>

    <p className="relatorio-meta-explicativo">
      Este gráfico mostra quantos seguidores a conta ganhou em cada dia do período selecionado.
    </p>

    <div className="relatorio-meta-grafico">
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart
          margin={{ top: 28, right: 55, left: 0, bottom: 5 }}
          data={followersDiario.map((item) => ({
            date: item.date,
            followers_gained: Number(item.followers_gained || 0),
            followers_total: Number(item.followers_total || 0),
          }))}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#e5e7eb"
          />

          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: "#6b7280" }}
            tickMargin={8}
            axisLine={false}
            tickLine={false}
            tickFormatter={(tick) => {
              const [ano, mes, dia] = String(tick).split("-");
              return `${dia}/${mes}`;
            }}
          />

          <YAxis
            yAxisId="gained"
            orientation="left"
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "#6b7280" }}
            tickMargin={8}
            axisLine={false}
            tickLine={false}
            domain={[0, "dataMax + 3"]}
          />

          <YAxis
            yAxisId="total"
            orientation="right"
            tick={{ fontSize: 10, fill: "#2563eb" }}
            tickMargin={8}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => Number(v).toLocaleString("pt-BR")}
            domain={["auto", "auto"]}
            label={{
              value: "Total de seguidores",
              angle: 90,
              position: "insideRight",
              offset: 8,
              style: { fontSize: 11, fill: "#2563eb" },
            }}
          />

          <Tooltip
            contentStyle={{
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              fontSize: "12px",
            }}
            formatter={(value, name) => {
              if (name === "Seguidores ganhos") {
                return [`+${Number(value).toLocaleString("pt-BR")}`, "Seguidores ganhos"];
              }

              if (name === "Total de seguidores") {
                return [Number(value).toLocaleString("pt-BR"), "Total de seguidores"];
              }

              return [value, name];
            }}
            labelFormatter={(label) => {
              const [ano, mes, dia] = String(label).split("-");
              return `${dia}/${mes}/${ano}`;
            }}
          />

          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />

          <Bar
            yAxisId="gained"
            dataKey="followers_gained"
            name="Seguidores ganhos"
            fill="#14b8a6"
            fillOpacity={0.85}
            radius={[6, 6, 0, 0]}
            barSize={44}
          >
            <LabelList
              dataKey="followers_gained"
              position="top"
              formatter={(value) => (Number(value) > 0 ? `+${value}` : "")}
              style={{
                fill: "#0f766e",
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          </Bar>

          <Line
            yAxisId="total"
            type="monotone"
            dataKey="followers_total"
            name="Total de seguidores"
            stroke="#2563eb"
            strokeWidth={2.5}
            dot={{ r: 3, fill: "#2563eb", strokeWidth: 0 }}
            activeDot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>

    <hr className="linha-pontilhada" />
  </>
)}
{/* 🔵 Distribuição geográfica dos seguidores (Instagram) */}
{dados?.instagram_cidades?.data?.[0]?.total_value?.breakdowns?.[0]?.results?.length > 0 && (() => {
  const cidadesRaw = dados.instagram_cidades.data[0].total_value.breakdowns[0].results || [];

  const cidadesOrdenadas = cidadesRaw
    .map((item) => ({
      cidade: item.dimension_values?.[0] || "Cidade não informada",
      seguidores: Number(item.value || 0),
    }))
    .filter((item) => item.seguidores > 0)
    .sort((a, b) => b.seguidores - a.seguidores);

  const totalSeguidoresMapeados = cidadesOrdenadas.reduce(
    (acc, item) => acc + item.seguidores,
    0
  );

  const seguidoresTotaisInstagram = Number(
  dados?.instagram_perfil?.followers_count || 0
);

const basePercentual = seguidoresTotaisInstagram > 0
  ? seguidoresTotaisInstagram
  : totalSeguidoresMapeados;

const seguidoresSemCidade = seguidoresTotaisInstagram > 0
  ? Math.max(0, seguidoresTotaisInstagram - totalSeguidoresMapeados)
  : 0;

const coberturaGeografica = seguidoresTotaisInstagram > 0
  ? (totalSeguidoresMapeados / seguidoresTotaisInstagram) * 100
  : 100;

  const top10 = cidadesOrdenadas.slice(0, 10);
  const top3 = cidadesOrdenadas.slice(0, 3);

  const seguidoresTop3 = top3.reduce((acc, item) => acc + item.seguidores, 0);
const percentualTop3 = basePercentual > 0
  ? (seguidoresTop3 / basePercentual) * 100
  : 0;

  const cidadePrincipal = cidadesOrdenadas[0];

  const seguidoresOutrasCidades = cidadesOrdenadas
    .slice(10)
    .reduce((acc, item) => acc + item.seguidores, 0);

function encurtarCidade(nome, limite = 13) {
  const texto = String(nome || "");

  const semUf = texto.replace(/,\s*[A-Z]{2}$/i, "").trim();

  if (semUf.length <= limite) return semUf;

  return `${semUf.slice(0, limite).trim()}...`;
}

function CidadeTick({ x, y, payload }) {
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fill="#374151"
      fontSize={11}
      style={{
        whiteSpace: "nowrap",
      }}
    >
      {encurtarCidade(payload.value, 13)}
    </text>
  );
}

const dadosGraficoCidades = top10.map((item) => ({
  cidade: item.cidade,
  seguidores: item.seguidores,
participacao: basePercentual > 0
  ? (item.seguidores / basePercentual) * 100
  : 0,
}));

  const resumoTop3 = top3.map((item) => item.cidade).join(", ");

  return (
    <>
      <h2 className="relatorio-meta-subtitulo" style={{ borderBottom: "none" }}>
        Distribuição geográfica dos seguidores no Instagram
      </h2>

<p className="relatorio-meta-explicativo">
  Esta seção mostra onde estão concentrados os seguidores da conta do Instagram.
  O total de seguidores vem do perfil, enquanto a distribuição por cidade representa
  apenas os seguidores que a Meta conseguiu associar a uma localização.
</p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
          marginTop: "22px",
          marginBottom: "28px",
        }}
      >
<div className="relatorio-meta-card">
  <span>Seguidores totais</span>
  <strong>
    {seguidoresTotaisInstagram > 0
      ? formatarNumero(seguidoresTotaisInstagram)
      : "-"}
  </strong>
  <p className="relatorio-meta-texto-cards">
    Total real de seguidores informado pelo Instagram
  </p>
</div>

<div className="relatorio-meta-card">
  <span>Com cidade identificada</span>
  <strong>{formatarNumero(totalSeguidoresMapeados)}</strong>
  <p className="relatorio-meta-texto-cards">
    {seguidoresTotaisInstagram > 0
      ? `${formatarPorcentagem(coberturaGeografica)} da base total`
      : "Total das cidades retornadas pela Meta"}
  </p>
</div>

<div className="relatorio-meta-card">
  <span>Sem cidade identificada</span>
  <strong>
    {seguidoresTotaisInstagram > 0
      ? formatarNumero(seguidoresSemCidade)
      : "-"}
  </strong>
  <p className="relatorio-meta-texto-cards">
    Seguidores que não entraram na quebra por cidade
  </p>
</div>

<div className="relatorio-meta-card">
  <span>Cidade principal</span>
  <strong>{cidadePrincipal?.cidade || "-"}</strong>
  <p className="relatorio-meta-texto-cards">
    {cidadePrincipal
      ? `${formatarPorcentagem(
          (cidadePrincipal.seguidores / basePercentual) * 100
        )} da base total`
      : "Sem dados suficientes"}
  </p>
</div>

<div className="relatorio-meta-card">
  <span>Cidades mapeadas</span>
  <strong>{formatarNumero(cidadesOrdenadas.length)}</strong>
  <p className="relatorio-meta-texto-cards">
    Quantidade de cidades identificadas na audiência
  </p>
</div>
      </div>

      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "18px",
          padding: "20px",
          marginBottom: "28px",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
        }}
      >
        <h3
          style={{
            margin: "0 0 6px",
            fontSize: "17px",
            color: "#111827",
          }}
        >
          Leitura rápida
        </h3>

        <p
          style={{
            margin: 0,
            color: "#4b5563",
            fontSize: "14px",
            lineHeight: 1.6,
          }}
        >
          A maior concentração de seguidores está em{" "}
          <b>{cidadePrincipal?.cidade || "cidade não identificada"}</b>.
          As três principais cidades representam{" "}
          <b>{formatarPorcentagem(percentualTop3)}</b> da base mapeada.
          Isso ajuda a entender onde a marca já tem mais força orgânica e onde
          pode fazer sentido reforçar conteúdo, relacionamento ou campanhas locais.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 0.95fr) minmax(420px, 1.05fr)",
          gap: "28px",
          alignItems: "start",
        }}
      >
        {/* Tabela Top 10 Cidades */}
        <div className="relatorio-meta-tabela-wrapper" style={{ marginTop: 0 }}>
          <table className="relatorio-meta-tabela">
            <thead>
              <tr>
                <th style={{ width: "52px", textAlign: "center" }}>#</th>
                <th>Cidade</th>
                <th style={{ textAlign: "center" }}>Seguidores</th>
                <th style={{ textAlign: "center" }}>% do total</th>
              </tr>
            </thead>

            <tbody>
              {top10.map((item, index) => {
const percentual = basePercentual > 0
  ? (item.seguidores / basePercentual) * 100
  : 0;

                return (
                  <tr key={`${item.cidade}-${index}`}>
                    <td style={{ textAlign: "center", fontWeight: 700 }}>
                      {index + 1}
                    </td>

                    <td>{item.cidade}</td>

                    <td style={{ textAlign: "center" }}>
                      {formatarNumero(item.seguidores)}
                    </td>

                    <td style={{ textAlign: "center" }}>
                      {formatarPorcentagem(percentual)}
                    </td>
                  </tr>
                );
              })}

              {seguidoresOutrasCidades > 0 && (
                <tr>
                  <td style={{ textAlign: "center", fontWeight: 700 }}>+</td>
                  <td>Outras cidades</td>
                  <td style={{ textAlign: "center" }}>
                    {formatarNumero(seguidoresOutrasCidades)}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {formatarPorcentagem(
                      (seguidoresOutrasCidades / basePercentual) * 100
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

 {/* Gráfico de Barras */}
<div className="relatorio-meta-grafico" style={{ marginTop: 0 }}>
  <h3
    style={{
      margin: "0 0 18px",
      fontSize: "18px",
      fontWeight: 700,
      color: "#1f2937",
      lineHeight: 1.2,
      textAlign: "left",
    }}
  >
    Top 10 cidades
  </h3>

  <ResponsiveContainer width="100%" height={420}>
    <BarChart
      data={dadosGraficoCidades}
      layout="vertical"
      margin={{ top: 8, right: 42, left: 34, bottom: 8 }}
    >
      <CartesianGrid strokeDasharray="3 3" horizontal={false} />

      <XAxis
        type="number"
        tick={{ fontSize: 11, fill: "#6b7280" }}
        tickFormatter={(value) => Number(value).toLocaleString("pt-BR")}
      />

      <YAxis
        type="category"
        dataKey="cidade"
        width={92}
        interval={0}
        tick={<CidadeTick />}
        tickLine={false}
        axisLine={false}
      />

      <Tooltip
        contentStyle={{
          borderRadius: "10px",
          border: "1px solid #e5e7eb",
          fontSize: "12px",
        }}
        labelFormatter={(label) => label}
        formatter={(value, name, props) => {
          const participacao = props?.payload?.participacao || 0;

          return [
            `${Number(value).toLocaleString("pt-BR")} seguidores (${formatarPorcentagem(participacao)})`,
            "Seguidores",
          ];
        }}
      />

      <Bar
        dataKey="seguidores"
        name="Seguidores"
        fill="#2563eb"
        radius={[0, 8, 8, 0]}
      >
        <LabelList
          dataKey="seguidores"
          position="right"
          formatter={(value) => Number(value).toLocaleString("pt-BR")}
          style={{
            fill: "#374151",
            fontSize: 11,
            fontWeight: 600,
          }}
        />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
</div>
</div>
      <hr className="linha-pontilhada" />
    </>
  );
})()}

{/* 🔵 Resultado por Gênero */}
{(() => {
  const nomeGenero = (gender) => {
    if (gender === "female") return "Feminino";
    if (gender === "male") return "Masculino";
    return "Desconhecido";
  };

  const corGenero = (gender) => {
    if (gender === "female") return "#ec4899";
    if (gender === "male") return "#2563eb";
    return "#9ca3af";
  };

  const ordemGenero = {
    female: 1,
    male: 2,
    unknown: 3,
  };

  const agrupadoGenero = {};

  (dados.meta_ads_age_gender?.data || [])
    .filter(pertenceACampanhaSelecionada)
    .forEach((item) => {
      const genero = item.gender || "unknown";

      if (!agrupadoGenero[genero]) {
        agrupadoGenero[genero] = {
          gender: genero,
          nome: nomeGenero(genero),
          conversas: 0,
          reach: 0,
          impressions: 0,
          spend: 0,
        };
      }

      (item.actions || []).forEach((a) => {
        if (a.action_type === "onsite_conversion.messaging_conversation_started_7d") {
          agrupadoGenero[genero].conversas += parseInt(a.value || 0, 10);
        }
      });

      agrupadoGenero[genero].reach += parseInt(item.reach || 0, 10);
      agrupadoGenero[genero].impressions += parseInt(item.impressions || 0, 10);
      agrupadoGenero[genero].spend += parseFloat(item.spend || 0);
    });

  const dadosGenero = Object.values(agrupadoGenero)
    .map((g) => ({
      ...g,
      cpl: g.conversas > 0 ? g.spend / g.conversas : null,
      cpm: g.reach > 0 ? g.spend / (g.reach / 1000) : null,
    }))
    .sort((a, b) => {
      const ordemA = ordemGenero[a.gender] || 99;
      const ordemB = ordemGenero[b.gender] || 99;
      return ordemA - ordemB;
    });

  if (dadosGenero.length === 0) return null;

  const totalReachGenero = dadosGenero.reduce((acc, g) => acc + g.reach, 0);
  const totalConversasGenero = dadosGenero.reduce((acc, g) => acc + g.conversas, 0);
  const totalSpendGenero = dadosGenero.reduce((acc, g) => acc + g.spend, 0);

  const maiorAlcance = [...dadosGenero].sort((a, b) => b.reach - a.reach)[0];
  const maisMensagens = [...dadosGenero].sort((a, b) => b.conversas - a.conversas)[0];

  const melhorCpl = [...dadosGenero]
    .filter((g) => g.conversas > 0 && g.cpl !== null)
    .sort((a, b) => a.cpl - b.cpl)[0];

  const percentualAlcanceMaior = totalReachGenero > 0
    ? (maiorAlcance.reach / totalReachGenero) * 100
    : 0;

  const percentualMensagemMaior = totalConversasGenero > 0
    ? (maisMensagens.conversas / totalConversasGenero) * 100
    : 0;

  return (
    <>
      <h2 className="relatorio-meta-subtitulo" style={{ borderBottom: "none" }}>
        Resultado por Gênero
      </h2>

      <p className="relatorio-meta-explicativo">
        Comparativo de desempenho entre os públicos feminino, masculino e desconhecido,
        considerando alcance, mensagens iniciadas, investimento e custo por lead.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
          marginTop: "22px",
          marginBottom: "28px",
        }}
      >
        <div className="relatorio-meta-card">
          <span>Maior alcance</span>
          <strong>{maiorAlcance?.nome || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {maiorAlcance
              ? `${formatarNumero(maiorAlcance.reach)} pessoas alcançadas`
              : "Sem dados suficientes"}
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Mais mensagens</span>
          <strong>{maisMensagens?.nome || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {maisMensagens
              ? `${formatarNumero(maisMensagens.conversas)} conversas iniciadas`
              : "Sem dados suficientes"}
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Melhor CPL</span>
          <strong>{melhorCpl?.nome || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {melhorCpl
              ? `${formatarMoeda(melhorCpl.cpl)} por conversa`
              : "Sem conversas suficientes"}
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Investimento analisado</span>
          <strong>{formatarMoeda(totalSpendGenero)}</strong>
          <p className="relatorio-meta-texto-cards">
            Soma do investimento com quebra por gênero
          </p>
        </div>
      </div>

      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "18px",
          padding: "20px",
          marginBottom: "28px",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
        }}
      >
        <h3
          style={{
            margin: "0 0 6px",
            fontSize: "17px",
            color: "#111827",
          }}
        >
          Leitura rápida
        </h3>

        <p
          style={{
            margin: 0,
            color: "#4b5563",
            fontSize: "14px",
            lineHeight: 1.6,
          }}
        >
          O público <b>{maiorAlcance?.nome || "com maior alcance"}</b> concentrou{" "}
          <b>{formatarPorcentagem(percentualAlcanceMaior)}</b> do alcance.
          Já o público <b>{maisMensagens?.nome || "com mais mensagens"}</b> concentrou{" "}
          <b>{formatarPorcentagem(percentualMensagemMaior)}</b> das mensagens iniciadas.
          Quando alcance e mensagens não acompanham a mesma proporção, vale revisar criativos,
          segmentação e orçamento por público.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "18px",
          marginBottom: "28px",
        }}
      >
        {dadosGenero.map((g) => {
          const percentualAlcance = totalReachGenero > 0
            ? (g.reach / totalReachGenero) * 100
            : 0;

          const percentualMensagens = totalConversasGenero > 0
            ? (g.conversas / totalConversasGenero) * 100
            : 0;

          const cor = corGenero(g.gender);

          return (
            <div
              key={g.gender}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "18px",
                padding: "18px",
                background: "#fff",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "14px",
                  alignItems: "center",
                  marginBottom: "14px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 800,
                      color: "#111827",
                    }}
                  >
                    {g.nome}
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "3px",
                    }}
                  >
                    {formatarNumero(g.reach)} pessoas alcançadas
                  </div>
                </div>

                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "999px",
                    background: cor,
                    flexShrink: 0,
                  }}
                />
              </div>

              <div style={{ display: "grid", gap: "13px" }}>
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "12px",
                      marginBottom: "6px",
                      color: "#374151",
                      fontWeight: 600,
                    }}
                  >
                    <span>Participação no alcance</span>
                    <span>{formatarPorcentagem(percentualAlcance)}</span>
                  </div>

                  <div
                    style={{
                      height: "9px",
                      borderRadius: "999px",
                      background: "#eef2f7",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, percentualAlcance)}%`,
                        height: "100%",
                        borderRadius: "999px",
                        background: cor,
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "12px",
                      marginBottom: "6px",
                      color: "#374151",
                      fontWeight: 600,
                    }}
                  >
                    <span>Participação nas mensagens</span>
                    <span>{formatarPorcentagem(percentualMensagens)}</span>
                  </div>

                  <div
                    style={{
                      height: "9px",
                      borderRadius: "999px",
                      background: "#eef2f7",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, percentualMensagens)}%`,
                        height: "100%",
                        borderRadius: "999px",
                        background: cor,
                        opacity: 0.7,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                  marginTop: "16px",
                  paddingTop: "14px",
                  borderTop: "1px solid #eef2f7",
                }}
              >
                <div>
                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                    Mensagens
                  </div>
                  <strong style={{ fontSize: "14px", color: "#111827" }}>
                    {formatarNumero(g.conversas)}
                  </strong>
                </div>

                <div>
                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                    CPL
                  </div>
                  <strong style={{ fontSize: "14px", color: "#111827" }}>
                    {g.cpl !== null ? formatarMoeda(g.cpl) : "-"}
                  </strong>
                </div>

                <div>
                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                    CPM
                  </div>
                  <strong style={{ fontSize: "14px", color: "#111827" }}>
                    {g.cpm !== null ? formatarMoeda(g.cpm) : "-"}
                  </strong>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="relatorio-meta-tabela-wrapper">
        <table className="relatorio-meta-tabela tabela-genero">
          <thead>
            <tr>
              <th>Gênero</th>
              <th>Mensagens</th>
              <th>% Mensagens</th>
              <th>Alcance</th>
              <th>% Alcance</th>
              <th>Impressões</th>
              <th>Valor Investido</th>
              <th>CPL</th>
              <th>CPM Médio</th>
            </tr>
          </thead>

          <tbody>
            {dadosGenero.map((g, idx) => {
              const percentualAlcance = totalReachGenero > 0
                ? (g.reach / totalReachGenero) * 100
                : 0;

              const percentualMensagens = totalConversasGenero > 0
                ? (g.conversas / totalConversasGenero) * 100
                : 0;

              return (
                <tr key={idx}>
                  <td>{g.nome}</td>
                  <td>{formatarNumero(g.conversas)}</td>
                  <td>{formatarPorcentagem(percentualMensagens)}</td>
                  <td>{formatarNumero(g.reach)}</td>
                  <td>{formatarPorcentagem(percentualAlcance)}</td>
                  <td>{formatarNumero(g.impressions)}</td>
                  <td>{formatarMoeda(g.spend)}</td>
                  <td>{g.cpl !== null ? formatarMoeda(g.cpl) : "-"}</td>
                  <td>{g.cpm !== null ? formatarMoeda(g.cpm) : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <hr className="linha-pontilhada" />
    </>
  );
})()}

{/* 🔵 Resultado por Plataforma */}
{dadosGraficoConversasPlataforma.length > 0 && (() => {
  const dadosPlataformas = [...dadosGraficoConversasPlataforma]
    .map((item) => ({
      ...item,
      conversas: Number(item.conversas || 0),
    }))
    .filter((item) => item.conversas > 0)
    .sort((a, b) => b.conversas - a.conversas);

  const totalMensagensPlataforma = dadosPlataformas.reduce(
    (acc, item) => acc + item.conversas,
    0
  );

  const plataformaPrincipal = dadosPlataformas[0] || null;
  const segundaPlataforma = dadosPlataformas[1] || null;

  const percentualPrincipal = plataformaPrincipal && totalMensagensPlataforma > 0
    ? (plataformaPrincipal.conversas / totalMensagensPlataforma) * 100
    : 0;

  const percentualSegunda = segundaPlataforma && totalMensagensPlataforma > 0
    ? (segundaPlataforma.conversas / totalMensagensPlataforma) * 100
    : 0;

  const canaisAtivos = dadosPlataformas.length;

  const corPlataforma = (nome) => {
    const n = String(nome || "").toLowerCase();

    if (n.includes("instagram")) return "#e1306c";
    if (n.includes("facebook")) return "#1877f2";
    if (n.includes("messenger")) return "#00b2ff";
    if (n.includes("audience")) return "#7c3aed";

    return "#6b7280";
  };

  const textoLeituraRapida = (() => {
    if (!plataformaPrincipal) {
      return "Não há dados suficientes para interpretar a distribuição por plataforma.";
    }

    if (percentualPrincipal >= 70) {
      return `A maior parte das conversas veio do ${plataformaPrincipal.nome}, que concentrou ${formatarPorcentagem(percentualPrincipal)} das mensagens iniciadas. Isso mostra uma dependência forte dessa plataforma no período analisado.`;
    }

    if (segundaPlataforma && percentualSegunda >= 25) {
      return `O ${plataformaPrincipal.nome} foi a principal plataforma, mas o ${segundaPlataforma.nome} também teve participação relevante. Isso indica uma distribuição mais saudável das conversas entre canais.`;
    }

    return `O ${plataformaPrincipal.nome} foi a principal plataforma de geração de conversas no período, concentrando ${formatarPorcentagem(percentualPrincipal)} das mensagens iniciadas.`;
  })();

  return (
    <>
      <h2 className="relatorio-meta-subtitulo" style={{ borderBottom: "none" }}>
        Resultado por Plataforma
      </h2>

      <p className="relatorio-meta-explicativo">
        Distribuição das conversas iniciadas conforme a plataforma onde o anúncio foi exibido.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
          marginTop: "22px",
          marginBottom: "28px",
        }}
      >
        <div className="relatorio-meta-card">
          <span>Plataforma principal</span>
          <strong>{plataformaPrincipal?.nome || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {plataformaPrincipal
              ? `${formatarNumero(plataformaPrincipal.conversas)} mensagens | ${formatarPorcentagem(percentualPrincipal)}`
              : "Sem dados suficientes"}
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Segunda plataforma</span>
          <strong>{segundaPlataforma?.nome || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {segundaPlataforma
              ? `${formatarNumero(segundaPlataforma.conversas)} mensagens | ${formatarPorcentagem(percentualSegunda)}`
              : "Sem segunda plataforma relevante"}
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Total de mensagens</span>
          <strong>{formatarNumero(totalMensagensPlataforma)}</strong>
          <p className="relatorio-meta-texto-cards">
            Conversas atribuídas às plataformas de exibição
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Canais ativos</span>
          <strong>{formatarNumero(canaisAtivos)}</strong>
          <p className="relatorio-meta-texto-cards">
            Plataformas que geraram pelo menos uma conversa
          </p>
        </div>
      </div>

      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "18px",
          padding: "20px",
          marginBottom: "28px",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
        }}
      >
        <h3
          style={{
            margin: "0 0 6px",
            fontSize: "17px",
            color: "#111827",
          }}
        >
          Leitura rápida
        </h3>

        <p
          style={{
            margin: 0,
            color: "#4b5563",
            fontSize: "14px",
            lineHeight: 1.6,
          }}
        >
          {textoLeituraRapida}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gap: "14px",
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "18px",
          padding: "20px",
          marginBottom: "28px",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
        }}
      >
        <h3
          style={{
            margin: "0 0 4px",
            fontSize: "18px",
            fontWeight: 700,
            color: "#1f2937",
          }}
        >
          Participação por plataforma
        </h3>

        {dadosPlataformas.map((item) => {
          const percentual = totalMensagensPlataforma > 0
            ? (item.conversas / totalMensagensPlataforma) * 100
            : 0;

          const cor = corPlataforma(item.nome);

          return (
            <div key={item.nome}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr 150px",
                  gap: "14px",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#111827",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "999px",
                      background: cor,
                      flexShrink: 0,
                    }}
                  />
                  {item.nome}
                </div>

                <div
                  style={{
                    height: "12px",
                    borderRadius: "999px",
                    background: "#eef2f7",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(1, Math.min(100, percentual))}%`,
                      height: "100%",
                      borderRadius: "999px",
                      background: cor,
                    }}
                  />
                </div>

                <div
                  style={{
                    textAlign: "right",
                    fontSize: "13px",
                    color: "#374151",
                    fontWeight: 700,
                  }}
                >
                  {formatarPorcentagem(percentual)}
                  <span
                    style={{
                      display: "block",
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "#6b7280",
                      marginTop: "2px",
                    }}
                  >
                    {formatarNumero(item.conversas)} mensagens
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="relatorio-meta-tabela-wrapper">
        <table className="relatorio-meta-tabela tabela-plataformas">
          <thead>
            <tr>
              <th>Plataforma</th>
              <th>Mensagens</th>
              <th>Participação</th>
              <th>Leitura</th>
            </tr>
          </thead>

          <tbody>
            {dadosPlataformas.map((item, idx) => {
              const percentual = totalMensagensPlataforma > 0
                ? (item.conversas / totalMensagensPlataforma) * 100
                : 0;

              return (
                <tr key={idx}>
                  <td>{item.nome}</td>

                  <td>{formatarNumero(item.conversas)}</td>

                  <td>{formatarPorcentagem(percentual)}</td>

                  <td>
                    {idx === 0
                      ? "Principal origem das conversas"
                      : percentual >= 25
                      ? "Canal complementar relevante"
                      : "Baixa participação no período"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <hr className="linha-pontilhada" />
    </>
  );
})()}

{/* 🔵 Resultado por Faixa Etária */}
{(() => {
  const nomeFaixaEtaria = (age) => {
    const faixa = String(age || "").trim();

    if (!faixa || faixa.toLowerCase() === "unknown") {
      return "Desconhecido";
    }

    return faixa;
  };

  const ordemFaixas = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];

  const agrupadoFaixa = {};

  (dados.meta_ads_age_gender?.data || [])
    .filter(pertenceACampanhaSelecionada)
    .forEach((item) => {
      const faixaOriginal = item.age || "unknown";
      const faixaNome = nomeFaixaEtaria(faixaOriginal);

      if (!agrupadoFaixa[faixaNome]) {
        agrupadoFaixa[faixaNome] = {
          age: faixaNome,
          conversas: 0,
          reach: 0,
          impressions: 0,
          spend: 0,
        };
      }

      (item.actions || []).forEach((a) => {
        if (a.action_type === "onsite_conversion.messaging_conversation_started_7d") {
          agrupadoFaixa[faixaNome].conversas += parseInt(a.value || 0, 10);
        }
      });

      agrupadoFaixa[faixaNome].reach += parseInt(item.reach || 0, 10);
      agrupadoFaixa[faixaNome].impressions += parseInt(item.impressions || 0, 10);
      agrupadoFaixa[faixaNome].spend += parseFloat(item.spend || 0);
    });

const dadosFaixas = Object.values(agrupadoFaixa)
  .map((faixa) => ({
    ...faixa,
    cpl: faixa.conversas > 0 ? faixa.spend / faixa.conversas : null,
    cpm: faixa.reach > 0 ? faixa.spend / (faixa.reach / 1000) : null,
  }))
  .sort((a, b) => {
      const indexA = ordemFaixas.indexOf(a.age);
      const indexB = ordemFaixas.indexOf(b.age);

      const ordemA = indexA === -1 ? 999 : indexA;
      const ordemB = indexB === -1 ? 999 : indexB;

      return ordemA - ordemB;
    });

  if (dadosFaixas.length === 0) return null;

  const dadosFaixasGrafico = dadosFaixas.filter(
  (faixa) => faixa.age !== "Desconhecido"
);
const totalConversasFaixaGrafico = dadosFaixasGrafico.reduce(
  (acc, item) => acc + item.conversas,
  0
);

const totalReachFaixaGrafico = dadosFaixasGrafico.reduce(
  (acc, item) => acc + item.reach,
  0
);
  const totalConversasFaixa = dadosFaixas.reduce((acc, item) => acc + item.conversas, 0);
  const totalReachFaixa = dadosFaixas.reduce((acc, item) => acc + item.reach, 0);
  const totalSpendFaixa = dadosFaixas.reduce((acc, item) => acc + item.spend, 0);

  const faixaMaisMensagens = [...dadosFaixas].sort((a, b) => b.conversas - a.conversas)[0];
  const faixaMaiorAlcance = [...dadosFaixas].sort((a, b) => b.reach - a.reach)[0];

  const faixaMelhorCpl = [...dadosFaixas]
    .filter((item) => item.conversas > 0 && item.cpl !== null)
    .sort((a, b) => a.cpl - b.cpl)[0];

  const faixaMaiorInvestimento = [...dadosFaixas].sort((a, b) => b.spend - a.spend)[0];

  const percentualMensagensVencedora = totalConversasFaixa > 0
    ? (faixaMaisMensagens.conversas / totalConversasFaixa) * 100
    : 0;

  const percentualAlcanceVencedora = totalReachFaixa > 0
    ? (faixaMaiorAlcance.reach / totalReachFaixa) * 100
    : 0;

  const corFaixa = (age) => {
    if (age === "18-24") return "#38bdf8";
    if (age === "25-34") return "#2563eb";
    if (age === "35-44") return "#7c3aed";
    if (age === "45-54") return "#ec4899";
    if (age === "55-64") return "#f97316";
    if (age === "65+") return "#16a34a";

    return "#9ca3af";
  };

  const textoLeituraRapida = (() => {
    if (!faixaMaisMensagens) {
      return "Não há dados suficientes para interpretar o desempenho por faixa etária.";
    }

    const mesmaFaixa =
      faixaMaisMensagens?.age &&
      faixaMaiorAlcance?.age &&
      faixaMaisMensagens.age === faixaMaiorAlcance.age;

    if (mesmaFaixa) {
      return `A faixa ${faixaMaisMensagens.age} concentrou tanto o maior volume de mensagens quanto o maior alcance. Isso indica que, neste período, esse público foi o principal responsável pela entrega e também pela geração de conversas.`;
    }

    return `A faixa ${faixaMaisMensagens.age} gerou o maior volume de mensagens, enquanto a faixa ${faixaMaiorAlcance.age} concentrou o maior alcance. Quando isso acontece, vale avaliar se o orçamento está sendo entregue para quem realmente responde melhor aos anúncios.`;
  })();

  return (
    <>
      <h2 className="relatorio-meta-subtitulo" style={{ borderBottom: "none" }}>
        Resultado por Faixa Etária
      </h2>

      <p className="relatorio-meta-explicativo">
        Comparativo de desempenho por idade no período selecionado de{" "}
        <b>{formatarDataBrasil(dataInicio)}</b> até{" "}
        <b>{formatarDataBrasil(dataFim)}</b>, considerando alcance, mensagens,
        investimento e custo por lead.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
          marginTop: "22px",
          marginBottom: "28px",
        }}
      >
        <div className="relatorio-meta-card">
          <span>Mais mensagens</span>
          <strong>{faixaMaisMensagens?.age || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {faixaMaisMensagens
              ? `${formatarNumero(faixaMaisMensagens.conversas)} conversas | ${formatarPorcentagem(percentualMensagensVencedora)}`
              : "Sem dados suficientes"}
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Maior alcance</span>
          <strong>{faixaMaiorAlcance?.age || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {faixaMaiorAlcance
              ? `${formatarNumero(faixaMaiorAlcance.reach)} pessoas | ${formatarPorcentagem(percentualAlcanceVencedora)}`
              : "Sem dados suficientes"}
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Melhor CPL</span>
          <strong>{faixaMelhorCpl?.age || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {faixaMelhorCpl
              ? `${formatarMoeda(faixaMelhorCpl.cpl)} por conversa`
              : "Sem conversas suficientes"}
          </p>
        </div>

        <div className="relatorio-meta-card">
          <span>Maior investimento</span>
          <strong>{faixaMaiorInvestimento?.age || "-"}</strong>
          <p className="relatorio-meta-texto-cards">
            {faixaMaiorInvestimento
              ? `${formatarMoeda(faixaMaiorInvestimento.spend)} investidos`
              : "Sem dados suficientes"}
          </p>
        </div>
      </div>

      <div
  style={{
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "18px",
    padding: "20px",
    marginBottom: "28px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
  }}
>
  <h3
    style={{
      margin: "0 0 6px",
      fontSize: "18px",
      fontWeight: 700,
      color: "#1f2937",
    }}
  >
    Entrega x Resposta por idade
  </h3>

  <p
    style={{
      margin: "0 0 20px",
      color: "#6b7280",
      fontSize: "13px",
      lineHeight: 1.5,
    }}
  >
    A coluna da esquerda mostra para quais idades os anúncios foram mais entregues.
    A coluna da direita mostra quais idades realmente geraram conversas.
    Quando uma faixa tem muito alcance e poucas mensagens, ela recebeu entrega,
    mas respondeu pouco.
  </p>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: "26px",
      alignItems: "start",
    }}
  >
    {/* Alcance */}
    <div>
      <h4
        style={{
          margin: "0 0 14px",
          fontSize: "15px",
          color: "#374151",
          fontWeight: 800,
        }}
      >
        Quem mais recebeu os anúncios
      </h4>

      <div style={{ display: "grid", gap: "14px" }}>
        {[...dadosFaixasGrafico]
          .sort((a, b) => b.reach - a.reach)
          .map((faixa) => {
            const percentualAlcance = totalReachFaixaGrafico > 0
              ? (faixa.reach / totalReachFaixaGrafico) * 100
              : 0;

            const cor = corFaixa(faixa.age);

            return (
              <div key={`alcance-${faixa.age}`}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "6px",
                    fontSize: "13px",
                    color: "#111827",
                    fontWeight: 700,
                  }}
                >
                  <span>{faixa.age}</span>
                  <span>{formatarPorcentagem(percentualAlcance)}</span>
                </div>

                <div
                  style={{
                    height: "12px",
                    borderRadius: "999px",
                    background: "#eef2f7",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(1, Math.min(100, percentualAlcance))}%`,
                      height: "100%",
                      borderRadius: "999px",
                      background: cor,
                      opacity: 0.55,
                    }}
                  />
                </div>

                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "11px",
                    color: "#6b7280",
                  }}
                >
                  {formatarNumero(faixa.reach)} pessoas alcançadas
                </div>
              </div>
            );
          })}
      </div>
    </div>

    {/* Mensagens */}
    <div>
      <h4
        style={{
          margin: "0 0 14px",
          fontSize: "15px",
          color: "#374151",
          fontWeight: 800,
        }}
      >
        Quem mais gerou conversas
      </h4>

      <div style={{ display: "grid", gap: "14px" }}>
        {[...dadosFaixasGrafico]
          .sort((a, b) => b.conversas - a.conversas)
          .map((faixa) => {
const percentualMensagens = totalConversasFaixaGrafico > 0
  ? (faixa.conversas / totalConversasFaixaGrafico) * 100
  : 0;
            const cor = corFaixa(faixa.age);

            return (
              <div key={`mensagens-${faixa.age}`}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "6px",
                    fontSize: "13px",
                    color: "#111827",
                    fontWeight: 700,
                  }}
                >
                  <span>{faixa.age}</span>
                  <span>{formatarPorcentagem(percentualMensagens)}</span>
                </div>

                <div
                  style={{
                    height: "12px",
                    borderRadius: "999px",
                    background: "#eef2f7",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(1, Math.min(100, percentualMensagens))}%`,
                      height: "100%",
                      borderRadius: "999px",
                      background: cor,
                    }}
                  />
                </div>

                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "11px",
                    color: "#6b7280",
                  }}
                >
                  {formatarNumero(faixa.conversas)} mensagens iniciadas
                </div>
              </div>
            );
          })}
      </div>
    </div>
  </div>
</div>

<div
  className="relatorio-meta-tabela-wrapper"
  style={{
    overflowX: "auto",
    marginTop: "10px",
    width: "100%",
  }}
>
  <table
    className="relatorio-meta-tabela tabela-faixas"
    style={{
      width: "100%",
      minWidth: "860px",
      tableLayout: "fixed",
      borderCollapse: "collapse",
    }}
  >
    <colgroup>
      <col style={{ width: "14%" }} />
      <col style={{ width: "13%" }} />
      <col style={{ width: "14%" }} />
      <col style={{ width: "15%" }} />
      <col style={{ width: "14%" }} />
      <col style={{ width: "17%" }} />
      <col style={{ width: "13%" }} />
    </colgroup>

    <thead>
      <tr>
        <th style={{ padding: "12px 10px", whiteSpace: "nowrap" }}>
          Faixa Etária
        </th>

        <th style={{ padding: "12px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
          Mensagens
        </th>

        <th style={{ padding: "12px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
          % Mensagens
        </th>

        <th style={{ padding: "12px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
          Alcance
        </th>

        <th style={{ padding: "12px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
          % Alcance
        </th>

        <th style={{ padding: "12px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
          Investimento
        </th>

        <th style={{ padding: "12px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
          CPL
        </th>
      </tr>
    </thead>

    <tbody>
      {dadosFaixas.map((faixa, idx) => {
        const percentualMensagens = totalConversasFaixa > 0
          ? (faixa.conversas / totalConversasFaixa) * 100
          : 0;

        const percentualAlcance = totalReachFaixa > 0
          ? (faixa.reach / totalReachFaixa) * 100
          : 0;

        return (
          <tr key={idx}>
            <td
              style={{
                padding: "12px 10px",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {faixa.age}
            </td>

            <td
              style={{
                padding: "12px 10px",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {formatarNumero(faixa.conversas)}
            </td>

            <td
              style={{
                padding: "12px 10px",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {formatarPorcentagem(percentualMensagens)}
            </td>

            <td
              style={{
                padding: "12px 10px",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {formatarNumero(faixa.reach)}
            </td>

            <td
              style={{
                padding: "12px 10px",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {formatarPorcentagem(percentualAlcance)}
            </td>

            <td
              style={{
                padding: "12px 10px",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {formatarMoeda(faixa.spend)}
            </td>

            <td
              style={{
                padding: "12px 10px",
                textAlign: "center",
                whiteSpace: "nowrap",
                fontWeight: 700,
              }}
            >
              {faixa.cpl !== null ? formatarMoeda(faixa.cpl) : "-"}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
</div>

      <hr className="linha-pontilhada" />
    </>
  );
})()}

{/* 🔵 Tabela: Principais Anúncios */}
<h2 className="relatorio-meta-subtitulo" style={{ borderBottom: "none" }}>Principais Anúncios</h2>
<p className="relatorio-meta-explicativo">Principais anúncios veiculados, ordenados pela quantidade de conversas iniciadas</p>
<div className="relatorio-meta-tabela-wrapper">
  <table className="relatorio-meta-tabela tabela-anuncios">
    <thead>
      <tr>
        <th>Imagem</th>
        <th>Nome do Anúncio</th>
        <th>Campanha</th>
        <th>Público</th>
        <th>Alcance</th>
        <th>Impressões</th>
        <th>Mensagens</th>
        <th>Valor Investido</th>
      </tr>
    </thead>
    <tbody>
      {anuncios
        .filter(anuncio =>
          isCampanhaSelecionada(anuncio.campaign_id) &&
          anuncio.cliques > 0
        )
          .map((anuncio, idx) => (
          <tr key={idx}>
            <td>
              {anuncio.thumbnail ? (
                <img
                  src={anuncio.thumbnail}
                  alt="Anúncio"
                  width="60"
                  style={{ borderRadius: "8px" }}
                />
              ) : (
                "Sem Imagem"
              )}
            </td>
            <td>{anuncio.nome}</td>
            <td>{anuncio.campaign_name}</td>
            <td>{anuncio.adset_name || "—"}</td>
            <td>{anuncio.alcance.toLocaleString("pt-BR")}</td>
            <td>{anuncio.impressoes.toLocaleString("pt-BR")}</td>
            <td>{anuncio.conversas.toLocaleString("pt-BR")}</td>
            <td>{anuncio.valorGasto.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL"
            })}</td>
          </tr>
        ))}
    </tbody>
  </table>
</div>

<hr className="linha-pontilhada" />

{/* 🔵 Tabela: Campanhas */}
<h2 className="relatorio-meta-subtitulo" style={{ borderBottom: "none" }}>Campanhas</h2>
<p className="relatorio-meta-explicativo">
  Campanhas que receberam orçamento no período de <b>{formatarDataBrasil(dataInicio)}</b> até <b>{formatarDataBrasil(dataFim)}</b>.
</p>
<div className="relatorio-meta-tabela-wrapper">
  <table className="relatorio-meta-tabela tabela-campanhas">
    <thead>
      <tr>
        <th>Campanha</th>
        <th>Impressões</th>
        <th>Alcance</th>
        <th>Mensagens</th>
        <th>Investimento</th>
        <th>CTR (%)</th>
        <th>CPC (R$)</th>
      </tr>
    </thead>
    <tbody>
          {(dados.meta_ads.data || [])
            .filter(camp =>
              isCampanhaSelecionada(camp.campaign_id)
            )
            .map((camp, idx) => (
          <tr key={idx}>
            <td>{camp.campaign_name}</td>
            <td>{formatarNumero(camp.impressions)}</td>
            <td>{formatarNumero(camp.reach)}</td>
            <td>
                {formatarNumero(
                  (camp.actions || []).reduce((acc, a) => {
                    if (a.action_type === "onsite_conversion.messaging_conversation_started_7d") {
                      return acc + parseInt(a.value || 0);
                    }
                    return acc;
                  }, 0)
                )}
              </td>

            <td>{formatarMoeda(camp.spend)}</td>
            <td>{formatarPorcentagem(camp.ctr)}</td>
            <td>{formatarMoeda(camp.cpc)}</td>
          </tr>
        ))}
    </tbody>
  </table>
</div>

{/* Sessão Analise IA */}


<section className="gads-ai-wrap">
<div className="gads-ai-head gads-ai-head--stack">
  <div className="gads-ai-title">Análise e recomendações (IA)</div>

  <div className="gads-ai-actions gads-ai-actions--below">
    <button
      type="button"
      className={`gads-ai-btn ${iaLoading ? "is-loading" : ""}`}
      onClick={gerarAnaliseIA}
      disabled={iaLoading}
    >
      {iaLoading ? "Analisando..." : "Receber análise e recomendações"}
    </button>
  </div>
</div>



  {/* mensagem SOMENTE durante o loading */}
  {iaLoading && (
    <div className="gads-ai-hint is-loading">
      Esse processo pode demorar. Levante, estique-se e tome um café!
    </div>
  )}


  {iaError && <div className="gads-ai-error">{iaError}</div>}


{iaResult && !!iaMeta?.generated_at && (
  <div className="gads-ai-subhead">
    <span className="gads-ai-stamp">Atualizada em: {iaMeta.generated_at}</span>
  </div>
)}


  {iaResult && (
  <div className="gads-ai-collapse">
    <button
      type="button"
      className="gads-ai-iconbtn"
      onClick={() => setIaOpen(v => !v)}
      aria-label={iaOpen ? "Ocultar IA" : "Mostrar IA"}
      title={iaOpen ? "Ocultar" : "Mostrar"}
    >
      {iaOpen ? (
        <ArrowUpFromLine size={22} />
      ) : (
        <ArrowDownFromLine size={22} />
      )}
    </button>
  </div>
)}

{/* Corpo da análise — só aparece quando iaOpen = true */}
{iaOpen && iaResult && (
  <div className="gads-ai-body">
    <div className="gads-ai-grid">
      {iaResult?.executive_summary?.plain_language &&
        renderBullets(
          "Resumo (linguagem simples)",
          iaResult.executive_summary.plain_language,
          "blue"
        )}

      {iaResult?.executive_summary?.for_traffic_manager &&
        renderBullets(
          "Resumo (para gestor de tráfego)",
          iaResult.executive_summary.for_traffic_manager,
          "purple"
        )}

        {renderBullets("Tradução para o negócio", iaResult?.scorecard?.business_translation, "green")}
        {renderBullets("Checagem de desperdício", iaResult?.scorecard?.waste_check, "orange")}
        {renderBullets("Geografia", iaResult?.scorecard?.geo, "teal")}

        {renderBullets("O que funcionou", iaResult?.what_worked, "green")}
        {renderBullets("O que prejudicou", iaResult?.what_hurt, "red")}

        {renderBullets("Orçamento: como alocar", iaResult?.budget_allocation, "blue")}
        {renderBullets("Criativo e copy", iaResult?.creative_and_copy, "purple")}
        {renderBullets("Funil e rastreamento", iaResult?.funnel_and_tracking, "teal")}

        {renderBullets("Hoje (30 min)", iaResult?.action_plan?.today_30min, "green")}
        {renderBullets("Próximas 48 horas", iaResult?.action_plan?.next_48h, "blue")}
        {renderBullets("Próximas 2 semanas", iaResult?.action_plan?.next_2weeks, "purple")}

        {renderBullets("Regras de escala", iaResult?.action_plan?.scaling_rules, "green")}
        {renderBullets("Regras de corte", iaResult?.action_plan?.stop_rules, "red")}

        {renderBullets("Riscos e atenção", iaResult?.risks_and_watchouts, "orange")}
        {renderBullets("Limitações do relatório", iaResult?.limitations, "gray")}
      </div>
    </div>
  )}
</section>

</>
)}
</div>
</div>
</div></>
);
}

export default RelatorioMetaCompleto;
